import { zodResolver } from "@hookform/resolvers/zod";
import { Grid2, Box, Chip, Stack, Typography, IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
    useContext,
    useEffect,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { Controller, useForm } from "react-hook-form";

import ButtonComponent from "../../../components/ButtonComponent";
import DropdownSelectComponent from "../../../components/DropdownSelectComponent";
import ModalComponent from "../../../components/ModalComponent";
import TextFieldComponent from "../../../components/TextFieldComponent";
import { AppContext } from "../../../context/AppContextProvider";

import {
    type SchoolTransportationFormInput,
    SchoolTransportationFormSchema,
} from "../../../services/SchoolTransportation/schemas";
import type { ISchoolTransportationProps } from "../../../services/SchoolTransportation/types";

import {
    useCreateSchoolTransportationAsync,
    useUpdateSchoolTransportationAsync,
} from "../../../services/SchoolTransportation/hooks/useMutation";
import { useGetAllSchoolTransportationByLicenseQuery } from "../../../services/SchoolTransportation/hooks/useQuery";

import { getMoovsecVehiclesAsync } from "../../../services/Location";
import type { ILocationProps } from "../../../services/Location/types";

import { useGetAllResponsiblesForSelectionMutateAsync } from "../../../services/Recognition/Responsible/hooks/useMutation";
import { useGetAllStudentsForSelectionMutateAsync } from "../../../services/Recognition/Student/hooks/useMutation";
import type { ISelectionOption } from "../../../services/types";

const normalizePlate = (plate?: string) => plate?.trim().toUpperCase() || "";

interface SchoolTransportationEditionModalProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    transportationEdit?: ISchoolTransportationProps;
    initialVehicle?: ILocationProps | null;
    handleClose?: () => void;
}

const SchoolTransportationEditionModal = ({
    isOpen,
    setIsOpen,
    transportationEdit,
    initialVehicle,
    handleClose,
}: SchoolTransportationEditionModalProps) => {
    const { showSnackbar } = useContext(AppContext);
    const [studentsList, setStudentsList] = useState<ISelectionOption[]>([]);
    const [responsiblesList, setResponsiblesList] = useState<ISelectionOption[]>([]);

    const [moovsecVehicles, setMoovsecVehicles] = useState<ILocationProps[]>([]);
    const [isLoadingMoovsecVehicles, setIsLoadingMoovsecVehicles] = useState(false);
    const [moovsecExpanded, setMoovsecExpanded] = useState(true);

    const { data: transportations = [] } =
        useGetAllSchoolTransportationByLicenseQuery(
            { pageNumber: 1, pageSize: 9999 },
            { name: "" },
        );

    const { mutateAsync: getStudentsSelectionAsync } = useGetAllStudentsForSelectionMutateAsync();
    const { mutateAsync: getResponsiblesSelectionAsync } = useGetAllResponsiblesForSelectionMutateAsync();
    const { mutate: createTransportation, isLoading: isCreating } = useCreateSchoolTransportationAsync();
    const { mutate: updateTransportation, isLoading: isUpdating } = useUpdateSchoolTransportationAsync();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<SchoolTransportationFormInput>({
        resolver: zodResolver(SchoolTransportationFormSchema),
        defaultValues: {
            name: "",
            plate: "",
            brand: "",
            model: "",
            studentIds: [],
            responsibleIds: [],
        },
    });

    useEffect(() => {
        if (isOpen) {
            let mounted = true;

            const loadMoovsec = async () => {
                setIsLoadingMoovsecVehicles(true);
                try {
                    const v = await getMoovsecVehiclesAsync(true);
                    if (mounted) setMoovsecVehicles(v || []);
                } catch (err) {
                    console.error("Erro ao carregar veículos Moovsec", err);
                } finally {
                    if (mounted) setIsLoadingMoovsecVehicles(false);
                }
            };

            loadMoovsec();

            const loadDataAndPopulateForm = async () => {
                try {
                    const [studentsData, responsiblesData] = await Promise.all([
                        getStudentsSelectionAsync(undefined),
                        getResponsiblesSelectionAsync(undefined),
                    ]);

                    const loadedStudents = (studentsData || []) as ISelectionOption[];
                    const loadedResponsibles = (responsiblesData || []) as ISelectionOption[];

                    if (mounted) {
                        setStudentsList(loadedStudents);
                        setResponsiblesList(loadedResponsibles);
                    }

                    let formData: SchoolTransportationFormInput = {
                        name: "",
                        plate: "",
                        brand: "",
                        model: "",
                        studentIds: [],
                        responsibleIds: [],
                    };

                    if (transportationEdit) {
                        const currentStudentIds = transportationEdit.students?.map((s) => s.id ?? 0) || [];
                        const currentResponsibleIds = transportationEdit.responsibles?.map((r) => r.id ?? 0) || [];

                        formData = {
                            name: transportationEdit.name || "",
                            plate: transportationEdit.plate || "",
                            brand: transportationEdit.brand || "",
                            model: transportationEdit.model || "",
                            studentIds: currentStudentIds,
                            responsibleIds: currentResponsibleIds,
                        };
                    } else if (initialVehicle) {
                        formData = {
                            name: initialVehicle.modelName?.trim() ||
                                  initialVehicle.vehiclePlate?.trim() ||
                                  "Veículo Moovsec",
                            plate: initialVehicle.vehiclePlate?.trim().toUpperCase() || "",
                            brand: "",
                            model: initialVehicle.modelName?.trim() || "",
                            studentIds: [],
                            responsibleIds: [],
                        };
                    }

                    if (mounted) reset(formData);
                } catch (error) {
                    console.error("Erro ao carregar dados", error);
                    if (mounted) showSnackbar("Erro ao carregar listas.", "error");
                }
            };

            loadDataAndPopulateForm();

            return () => {
                mounted = false;
            };
        } else {
            reset({
                name: "",
                plate: "",
                brand: "",
                model: "",
                studentIds: [],
                responsibleIds: [],
            });
        }
    }, [
        isOpen,
        transportationEdit,
        initialVehicle,
        getStudentsSelectionAsync,
        getResponsiblesSelectionAsync,
        reset,
        showSnackbar,
    ]);

    // Transformado em Map para otimizar a busca O(1) dentro do loop renderizador
    const transportationByPlate = new Map(
        transportations.map((transportation) => [
            normalizePlate(transportation.plate),
            transportation,
        ]),
    );

    const onSubmit = (data: SchoolTransportationFormInput) => {
        const onSuccess = () => {
            showSnackbar(
                transportationEdit
                    ? "Transporte atualizado com sucesso."
                    : "Transporte cadastrado com sucesso.",
                "success",
            );
            handleCloseModal();
        };

        const onError = (error: unknown) => {
            const msg = error instanceof Error ? error.message : "Erro ao salvar transporte.";
            showSnackbar(msg, "error");
        };

        if (transportationEdit?.id) {
            updateTransportation(
                { id: transportationEdit.id, data },
                { onSuccess, onError },
            );
        } else {
            createTransportation({ data }, { onSuccess, onError });
        }
    };

    const handleCloseModal = () => {
        setIsOpen(false);
        reset();
        if (handleClose) handleClose();
    };

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleCloseModal}
            title={transportationEdit ? "Editar Transporte" : "Novo Transporte"}
        >
            <Box
                sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #2F3957FF",
                    background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box>
                        <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                            Veículos Moovsec
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#8c9ead" }}>
                            Clique em um veículo para preencher formulário.
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Chip
                            label={`${moovsecVehicles.length} veículo(s)`}
                            sx={{ color: "#fff", borderColor: "#516f7f" }}
                            variant="outlined"
                        />
                        <IconButton
                            size="small"
                            onClick={() => setMoovsecExpanded((s) => !s)}
                        >
                            {moovsecExpanded ? (
                                <ExpandLess sx={{ color: "#fff" }} />
                            ) : (
                                <ExpandMore sx={{ color: "#fff" }} />
                            )}
                        </IconButton>
                    </Box>
                </Box>

                {moovsecExpanded && (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 1.5 }}
                    >
                        {isLoadingMoovsecVehicles && (
                            <Typography sx={{ color: "#8c9ead", py: 1 }}>
                                Carregando...
                            </Typography>
                        )}
                        {!isLoadingMoovsecVehicles && moovsecVehicles.length === 0 && (
                            <Typography sx={{ color: "#8c9ead", py: 1 }}>
                                Nenhum veículo Moovsec.
                            </Typography>
                        )}
                        {!isLoadingMoovsecVehicles &&
                            moovsecVehicles.length > 0 &&
                            moovsecVehicles.map((v) => {
                                const isRegistered = transportationByPlate.has(
                                    normalizePlate(v.vehiclePlate),
                                );

                                return (
                                    <Box
                                        key={v.entityId}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: "1px solid #2F3957FF",
                                            backgroundColor: "#171f2f",
                                            minWidth: { xs: "100%", sm: 260 },
                                            flex: "1 1 260px",
                                        }}
                                    >
                                        <Stack spacing={1.25}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            color: "#fff",
                                                            fontWeight: 700,
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {v.vehiclePlate || v.entityId}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label={isRegistered ? "Concluído" : "Pendente"}
                                                    color={isRegistered ? "success" : "warning"}
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{ color: "#c6d2db", minHeight: 40 }}
                                            >
                                                {v.address || "Sem endereço"}
                                            </Typography>

                                            {!isRegistered && (
                                                <ButtonComponent
                                                    fullWidth
                                                    sx={{ mt: 0.25 }}
                                                    onClick={() => {
                                                        reset({
                                                            name: v.modelName?.trim() ||
                                                                  v.vehiclePlate?.trim() ||
                                                                  "Veículo Moovsec",
                                                            plate: v.vehiclePlate?.trim().toUpperCase() || "",
                                                            brand: "",
                                                            model: v.modelName?.trim() || "",
                                                            studentIds: [],
                                                            responsibleIds: [],
                                                        });
                                                    }}
                                                >
                                                    Usar veículo
                                                </ButtonComponent>
                                            )}
                                        </Stack>
                                    </Box>
                                );
                            })}
                    </Stack>
                )}
            </Box>
            <Grid2 container spacing={2} sx={{ mt: 1 }}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextFieldComponent
                                label="Nome *"
                                errorMessage={errors.name?.message}
                                value={value || ""}
                                onChangeText={(newValue) => onChange(newValue)}
                            />
                        )}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="plate"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextFieldComponent
                                label="Placa *"
                                errorMessage={errors.plate?.message}
                                value={value || ""}
                                onChangeText={(newValue) => onChange(newValue)}
                            />
                        )}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="brand"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextFieldComponent
                                label="Marca"
                                errorMessage={errors.brand?.message}
                                value={value || ""}
                                onChangeText={(newValue) => onChange(newValue)}
                            />
                        )}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="model"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextFieldComponent
                                label="Modelo"
                                errorMessage={errors.model?.message}
                                value={value || ""}
                                onChangeText={(newValue) => onChange(newValue)}
                            />
                        )}
                    />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="responsibleIds"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <DropdownSelectComponent
                                label="Responsáveis"
                                multiple
                                options={responsiblesList}
                                valueField="id"
                                labelField="name"
                                selectedValues={value || []}
                                onSelectedValues={onChange}
                                errorMessage={errors.responsibleIds?.message}
                            />
                        )}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                        name="studentIds"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <DropdownSelectComponent
                                label="Alunos"
                                multiple
                                options={studentsList}
                                valueField="id"
                                labelField="name"
                                selectedValues={value || []}
                                onSelectedValues={onChange}
                                errorMessage={errors.studentIds?.message}
                            />
                        )}
                    />
                </Grid2>
            </Grid2>

            <ButtonComponent
                fullWidth
                sx={{ mt: 3 }}
                onClick={handleSubmit(onSubmit)}
                loading={isCreating || isUpdating}
            >
                Salvar
            </ButtonComponent>
        </ModalComponent>
    );
};

export default SchoolTransportationEditionModal;